export const TRUST_LINKS = {
  githubIssues: "https://github.com/rk23/lifty/issues",
  tjCurrentWindgrams: "http://wxtofly.net/v2/windgrams.html",
  tjOriginalSite: "http://wxtofly.net/",
  tjWindgramReading: "http://wxtofly.net/windgramexplain.html",
  noaaHrrr: "https://rapidrefresh.noaa.gov/hrrr/",
  openMeteoDocs: "https://open-meteo.com/en/docs/gfs-api",
  openMeteoLicense: "https://open-meteo.com/en/licence",
  openStreetMapCopyright: "https://www.openstreetmap.org/copyright",
  openStreetMapAttribution:
    "https://osmfoundation.org/wiki/Licence/Attribution_Guidelines",
  openStreetMapTilePolicy: "https://operations.osmfoundation.org/policies/tiles/",
  mapLibreProject: "https://maplibre.org/",
  mapLibreLicense:
    "https://github.com/maplibre/maplibre-gl-js/blob/main/LICENSE.txt"
} as const;

export const FIXED_REFERENCE_SINK_MS = 1.16;
export const PRIVACY_CONTRACT_VERSION = "2026-08-30";

export const HOW_TO_READ_TERMS = [
  {
    term: "W*",
    description:
      "Deardorff's characteristic convective velocity scale for the mixed layer. It is a useful lift-strength signal, not your peak instantaneous vario reading."
  },
  {
    term: "B/S",
    description:
      "Buoyancy-shear ratio. Higher values favor buoyancy-dominant thermals; lower values mean shear is more able to stretch or break them."
  },
  {
    term: "Thermal top",
    description:
      "The parcel ceiling: where a lifted surface parcel stops rising in the modeled column."
  },
  {
    term: "Usable top",
    description:
      "The height where the modeled Lenschow-shaped updraft tapers down to the fixed 1.16 m/s reference sink. It is a practical soaring ceiling, not a promise."
  },
  {
    term: "Texture",
    description:
      "A smooth, textured, or rough proxy from B/S, wind shear, and gust spread. It is not measured turbulence or EDR."
  },
  {
    term: "Phase",
    description:
      "Building, steady, or decaying context from the heat-flux trend. It describes the day trend and does not rewrite W*."
  },
  {
    term: "Map density and episodes",
    description:
      "Density is de-jittered positive altitude gain along recorded climbs. Episodes are timestamped persistent circling climbs. Both are observed history, not permission."
  },
  {
    term: "MSL, model AGL, launch AGL",
    description:
      "MSL is above sea level. Model AGL is above the model's terrain. Launch AGL is above the registered launch, which matters when model terrain misses the ridge."
  },
  {
    term: "Evidence panel",
    description:
      "Click an hour to inspect the raw and derived numbers behind the labels instead of trusting a single summary."
  }
] as const;

export const METHODOLOGY_SECTIONS = [
  {
    heading: "Forecast source and grid",
    paragraphs: [
      "Lifty starts with NOAA's HRRR forecast as served through Open-Meteo. HRRR is an hourly updated, convection-allowing model with roughly 3 km horizontal resolution over the continental United States.",
      "The public windgram keeps the column in the model's own frame by requesting the nearest returned cell and disabling Open-Meteo's elevation downscaling. In practice that means `cell_selection=nearest` and `elevation=nan`, so the 2 m values, pressure, terrain, and upper-air column all describe the same grid cell.",
      "That choice makes the model easier to inspect, but it also means launch-scale ridges, rotor, drainage, and narrow convergence are below the model's native resolution."
    ]
  },
  {
    heading: "Lift, cloud, and usable height",
    paragraphs: [
      "Thermal top is a parcel calculation, not the model's boundary-layer-height field. Lifty lifts a surface parcel until it stops rising in the forecast sounding and shows that as thermal top.",
      "W* is Deardorff's characteristic convective velocity scale, exposed directly instead of being folded into a hidden score. Usable top applies the established Lenschow-shaped lift profile and marks the height where the modeled updraft tapers down to the fixed 1.16 m/s reference sink used by the public product until real equipment profiles exist.",
      "Cloudbase uses the surface dewpoint and Bolton's lifting-condensation-level formulation. If the parcel top stays below cloudbase, the day can still be soarable without cumulus."
    ]
  },
  {
    heading: "Wind sources and terrain borrowing",
    paragraphs: [
      "Near the ground, one neighboring HRRR cell can sometimes match launch terrain better than the home cell. When that reduces the terrain mismatch enough, Lifty can borrow only the reported 10 m, 80 m, and gust winds from that one surface source.",
      "Boundary-layer wind, B/S, shear, and the upper-air barbs stay on the home atmospheric column even when the near-surface wind is terrain-matched. That separation is deliberate: it preserves one column for mixed-layer diagnostics instead of publishing a hybrid atmosphere."
    ]
  },
  {
    heading: "Thermal character",
    paragraphs: [
      "Thermal character is an interpretation layer over the same forecast, not a second model. It exposes power, texture, organization, and phase with the evidence behind each label.",
      "Texture is a transparent proxy from B/S, vector shear, and gust spread. Phase comes from the sensible-heat-flux trend. HRRR at 3 km does not resolve individual thermal cores, streets, or measured turbulence, so these labels are evidence-backed hints, not direct observations."
    ]
  },
  {
    heading: "Track-derived thermal map",
    paragraphs: [
      "The climb-density layer aggregates de-jittered positive altitude gain along publicly posted tracks the project is permitted to use. A fixed five-fix median and small vertical hysteresis reduce recorder noise; they are display heuristics, not calibrated thermal detection or time-based smoothing. The layer does not classify every point as a thermal or guarantee that a bright area will work again today.",
      "The circling-summary layer is stricter. It needs timestamps and looks for persistent circling climbs rather than straight glides or ridge lift. Launch markers are inferred by clustering each track's first valid position, which is why they are described as recorded launch areas rather than verified takeoff points.",
      "Lifty publishes aggregate products from those tracks. It does not republish the original track downloads or pilot names, and public posting alone is not treated as a blanket public-domain rule. The filter omits fix-to-fix altitude changes over 40 m as possible discontinuities, so a genuine sparse climb can be undercounted, especially in an untimed KML."
    ]
  },
  {
    heading: "Time and freshness",
    paragraphs: [
      "Forecast valid times should be read in the launch timezone, not the viewer's browser timezone. The point is to answer when the day turns on at the site.",
      "Freshness is split into knowable pieces. Lifty can report when it fetched a forecast and, when upstream checks succeed, the newest HRRR cycle NOAA has published. Open-Meteo does not publish the exact HRRR cycle it served for a response, so that specific model initialization stays unknown instead of being guessed."
    ]
  },
  {
    heading: "Known limits",
    paragraphs: [
      "A 3 km atmospheric model cannot resolve every ridge face, thermal trigger, valley circulation, or hazard. The map shows historical lift geometry, not live air. The forecast shows inspectable evidence, not a launch recommendation.",
      "Lifty now has a small amount of archive-overlapping forecast and flight join evidence, including recent Wonderland IGCs, but that validation is still early, single-site, and small-sample. It does not establish generalized observational skill.",
      "Some older public map tracks still predate archive coverage, so the thermal map corpus is broader than the current forecast-validation join."
    ]
  }
] as const;

export const SOURCE_AND_LICENSE_ITEMS = [
  {
    label: "NOAA HRRR overview",
    href: TRUST_LINKS.noaaHrrr,
    note: "Official source for the hourly updated 3 km HRRR model description."
  },
  {
    label: "Open-Meteo GFS & HRRR API documentation",
    href: TRUST_LINKS.openMeteoDocs,
    note: "Documents `cell_selection`, elevation downscaling, timezone behavior, and returned grid coordinates."
  },
  {
    label: "Open-Meteo licence and attribution",
    href: TRUST_LINKS.openMeteoLicense,
    note: "Current attribution and licence terms for data served through Open-Meteo."
  },
  {
    label: "OpenStreetMap copyright",
    href: TRUST_LINKS.openStreetMapCopyright,
    note: "Copyright and ODbL notice for map data attribution."
  },
  {
    label: "OpenStreetMap attribution guidance",
    href: TRUST_LINKS.openStreetMapAttribution,
    note: "How attribution should be shown on a browsable map or produced work."
  },
  {
    label: "OpenStreetMap tile usage policy",
    href: TRUST_LINKS.openStreetMapTilePolicy,
    note: "Operational policy for apps that directly use tile.openstreetmap.org."
  },
  {
    label: "MapLibre project",
    href: TRUST_LINKS.mapLibreProject,
    note: "Project home for the WebGL map renderer used in the frontend."
  },
  {
    label: "MapLibre GL JS license",
    href: TRUST_LINKS.mapLibreLicense,
    note: "Current BSD-3-Clause license text for the map library."
  },
  {
    label: "TJ's current windgrams",
    href: TRUST_LINKS.tjCurrentWindgrams,
    note: "Current WXtoFly windgram surface."
  },
  {
    label: "Original WXtoFly site",
    href: TRUST_LINKS.tjOriginalSite,
    note: "Original WXtoFly home page."
  },
  {
    label: "TJ's Windgram Reading",
    href: TRUST_LINKS.tjWindgramReading,
    note: "TJ Olney's windgram reading guide."
  }
] as const;

export const LOCAL_PREFERENCE_ITEMS = [
  {
    key: "last_site",
    label: "Last selected launch",
    status: "shipped",
    detail: "Stored only in local browser storage so returning visitors reopen the last site."
  },
  {
    key: "unit_profile",
    label: "Unit preference",
    status: "shipped",
    detail: "Stored only on the device after the visitor explicitly chooses US or Metric."
  },
  {
    key: "thermal_map_camera",
    label: "Per-dataset map camera",
    status: "shipped",
    detail:
      "Stored only in local browser storage for that dataset so the map can reopen where you left it. The exact camera is not sent in product telemetry."
  }
] as const;

export const TELEMETRY_PRODUCT_EVENTS = [
  "forecast_loaded",
  "site_selected",
  "day_changed",
  "hour_inspected",
  "thermal_map_opened",
  "thermal_layer_switched",
  "map_zoom_bucket_reached",
  "static_svg_opened",
  "flight_mode_changed",
  "unit_profile_changed"
] as const;

export const TELEMETRY_FAILURE_EVENTS = [
  "api_failure",
  "load_failure",
  "render_failure"
] as const;

export const TELEMETRY_CONTEXT_FIELDS = [
  "site_slug",
  "day_offset",
  "selected_hour",
  "flight_mode",
  "unit_profile",
  "thermal_layer",
  "zoom_bucket",
  "failure_kind",
  "surface",
  "release_version",
  "release_channel"
] as const;

export const PRIVACY_FORBIDDEN_DATA = [
  "accounts",
  "cookies",
  "user ids",
  "pilot names",
  "private tracks",
  "precise map camera telemetry",
  "free text",
  "email capture inside Lifty"
] as const;

export const PRIVACY_CONTRACT = {
  version: PRIVACY_CONTRACT_VERSION,
  telemetryStatus: "shipped",
  telemetryScope:
    "Lifty sends anonymous, first-party, allowlisted product and failure events with low-cardinality product and release context only.",
  telemetryImplementationNote:
    "Events go to Lifty's same-origin telemetry endpoint without cookies, credentials, a referrer, stable user identifiers, free text, track contents, or precise map coordinates. Cloudflare may still retain ordinary platform request metadata in its infrastructure logs under its own operational controls.",
  linkExitNote:
    "Following a feedback link or another external source leaves Lifty and hands you to that destination's own terms and privacy policy."
} as const;
